import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';
import { useSearchParams } from 'react-router-dom';
import Notification from '../../../components/notification/notification';
import WeekSlider from '../../../components/week-slider/week-slider';
import orderApi from '../../../services/orderApi';
import financeApi from '../../../services/financeApi';
import NoItems from '../../../assets/no-items.svg';
import { TWDataTable } from '../../../components/ui';
import { headerCls } from '../../../data/columns';
import { StatGridSkeleton } from '../../../components/skeleton/skeleton';
import { DataTableSkeleton } from '../../../data/columns';
import { OrderStatus } from '../../../data/enums';
import NoStoreLogo from '../../../assets/no-store-logo.svg';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { setStartAndEndDate } from '../../../utils/setStartAndEndDate';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Tooltip } from 'react-tooltip';
dayjs.extend(customParseFormat);
const utc = require('dayjs/plugin/utc');
const timezone = require('dayjs/plugin/timezone');

dayjs.extend(utc);
dayjs.extend(timezone);

const OrderList = () => {
  const globalCompany = useSelector((state) => state.companyReducer.selected);
  const [searchParams, setSearchParams] = useSearchParams();
  const [finance, setFinance] = useState([]);
  const [thisWeekDays, setThisWeekDays] = useState({
    startdate: '',
    enddate: '',
  });
  const [filteredOrderList, setFilteredOrderList] = useState([]);
  const [thisWeekOrders, setThisWeekOrders] = useState([]);
  const [totalStats, setTotalStats] = useState([]);
  const [loading, setLoading] = useState({
    listLoading: true,
    upsertLoading: false,
    deleteLoading: false,
  });
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 20,
    totalRecords: null,
  });
  const [notf, setNotf] = useState({
    display: false,
    type: 'error',
    message: '',
  });
  const [isColumnVisible, setIsColumnVisible] = useState({
    crvtax: false,
    itemfee: false,
  });

  const handleWeekChange = (startOfWeek, endOfWeek) => {
    setThisWeekDays({ startdate: startOfWeek, enddate: endOfWeek });

    const newSearchParams = new URLSearchParams(searchParams.toString());

    newSearchParams.set(
      'startdate',
      setStartAndEndDate(startOfWeek, 'startdate', '0'),
    );
    newSearchParams.set(
      'enddate',
      setStartAndEndDate(endOfWeek, 'enddate', '0'),
    );

    setSearchParams(newSearchParams);
  };

  const getFinance = useCallback(async () => {
    setLoading((prev) => ({ ...prev, listLoading: true }));
    setFinance([]);
    setThisWeekOrders([]);
    try {
      const isThisWeek = searchParams.get('thisWeek');
      const storeid = searchParams.get('storeid');

      if (isThisWeek) {
        const startdate = searchParams.get('startdate');
        const enddate = searchParams.get('enddate');

        setThisWeekDays({ startdate, enddate });

        const query = {
          companyid: globalCompany.id,
          pageSize: 1000,
          paymentstatus: 20,
          storeid,
          startdate,
          enddate,
        };

        const list = await orderApi.list(query);
        if (list.data.data.length > 0) {
          setPagination({
            ...pagination,
            totalRecords: Math.ceil(list.data.data.length),
          });
          setThisWeekOrders(
            list.data.data.map((order) => {
              return {
                order,
                total: order.total,
              };
            }),
          );
          const totalStats = calculateThisWeekOrdersTotal(list.data.data);
          setTotalStats(totalStats);
          setFilteredOrderList(
            list.data.data
              .map((order) => {
                return {
                  order,
                  total: order.total,
                };
              })
              .sort((a, b) => {
                return (
                  new Date(b.order.createdate) - new Date(a.order.createdate)
                );
              })
              .slice(0, pagination.pageSize),
          );

          setIsColumnVisible({
            crvtax: list.data.data.some((order) => order.crvtax > 0),
            itemfee: list.data.data.some((order) => order.itemfee > 0),
          });
        } else {
          setFilteredOrderList([]);
          setTotalStats([
            { value: 0, name: 'Total Order' },
            { value: 0, name: 'Total Tax' },
            { value: 0, name: 'Total Tip' },
            { value: 0, name: 'Total Discount' },
            { value: 0, name: 'Total Refund' },
            { value: 0, name: 'Total Net Payout' },
          ]);
        }
      } else {
        const query = `?id=${searchParams.get('financeid')}`;

        const list = await financeApi.getById(query);
        setPagination({
          ...pagination,
          totalRecords: Math.ceil(list.data.data.details.length),
        });
        setFinance(list.data.data);
        calculateTotalOrderPrices(list.data.data.details);
        setFilteredOrderList(
          list.data.data.details
            .sort((a, b) => {
              return (
                new Date(b.order.createdate) - new Date(a.order.createdate)
              );
            })
            .slice(0, pagination.pageSize),
        );
        setIsColumnVisible({
          crvtax: list.data.data.details.some((order) => order.crvtax > 0),
          itemfee: list.data.data.details.some((order) => order.itemfee > 0),
        });
      }
    } catch (error) {
      setNotf((prev) => ({
        ...prev,
        message: error,
        display: true,
      }));
    } finally {
      setLoading((prev) => ({ ...prev, listLoading: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thisWeekDays.startdate]);

  const calculateThisWeekOrdersTotal = (orderList) => {
    const totalStats = [
      { value: 0, name: 'Total Order' },
      { value: 0, name: 'Total Tax' },
      { value: 0, name: 'Total Tip' },
      { value: 0, name: 'Total Discount' },
      { value: 0, name: 'Total Refund' },
      { value: 0, name: 'Total Net Payout' },
    ];

    const totals = orderList.reduce((acc, order) => {
      const updatedTotals = [...acc];

      updatedTotals[0].value += 1;
      updatedTotals[1].value += order.tax;
      updatedTotals[2].value += order.tip;
      updatedTotals[3].value += order.discount;
      updatedTotals[4].value += order.refund;
      updatedTotals[5].value +=
        order.subtotal +
        order.tip +
        order.crvtax +
        order.totalextrafee +
        order.tax -
        order.itemfee -
        ((order.stripefee > order.cardfee ? order.stripefee : order.cardfee) +
          order.discount +
          order.thiswayfee +
          order.refund);

      return updatedTotals;
    }, totalStats);

    return totals;
  };

  const handleChangePage = (pageNumber, pageSize) => {
    setPagination((prev) => ({ ...prev, pageNumber, pageSize }));

    const startIndex = (pageNumber - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    const orders = searchParams.get('thisWeek')
      ? thisWeekOrders
      : finance.details;

    const filteredList = orders
      .sort((a, b) => {
        return new Date(b.order.createdate) - new Date(a.order.createdate);
      })
      .slice(startIndex, endIndex);

    setFilteredOrderList(filteredList);
    setIsColumnVisible({
      crvtax: orders.some((order) => order.crvtax > 0),
      itemfee: orders.some((order) => order.itemfee > 0),
    });
  };

  const calculateTotalOrderPrices = (orderList) => {
    const totalStats = [
      { value: 0, name: 'Total Order' },
      { value: 0, name: 'Total Tax' },
      { value: 0, name: 'Total Tip' },
      { value: 0, name: 'Total Discount' },
      { value: 0, name: 'Total Refund' },
      { value: 0, name: 'Total Net Payout' },
    ];

    const totals = orderList.reduce((acc, order) => {
      const updatedTotals = [...acc];

      updatedTotals[0].value += 1;
      updatedTotals[1].value += order.order.tax;
      updatedTotals[2].value += order.order.tip;
      updatedTotals[3].value += order.order.discount;
      updatedTotals[4].value += order.order.refund;
      updatedTotals[5].value += order.total;

      return updatedTotals;
    }, totalStats);

    setTotalStats(totals);

    return totals;
  };

  const hasCrvTaxInOrders = (orderList) => {
    return orderList.some((order) => order?.order?.crvtax > 0);
  };

  const orderWithExtraFees = (orderList) => {
    return (
      orderList?.find((order) => order.order?.extrafees?.length > 0)
        ?.extrafees || []
    );
  };

  useEffect(() => {
    getFinance();
  }, [getFinance]);

  const formatUSD = (value) =>
    (value ?? 0).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });

  const formatTime = (order) =>
    order.order?.store?.timezoneid
      ? moment(order.order.createdate)
          .tz(order.order.store?.timezoneid)
          .format('MM/DD/YYYY hh:mm A')
      : moment(order.order.createdate)
          .utcOffset((order.order.store?.timezone || 0) * 60)
          .format('MM/DD/YYYY hh:mm A');

  const calcNetPayout = (row) => {
    const o = row.order;
    const cardFee = o.stripefee > o.cardfee ? o.stripefee : o.cardfee;
    const value =
      o.subtotal +
      o.tip +
      (o.crvtax || 0) +
      (o.totalextrafee || 0) +
      o.tax -
      (o.itemfee || 0) -
      (cardFee + o.discount + o.thiswayfee + o.refund);
    if (row.total < 0) return `(${formatUSD(value).replace('-', '')})`;
    return formatUSD(value);
  };

  const showStoreFees = orderWithExtraFees(filteredOrderList);

  const orderColumns = [
    {
      title: <span className={headerCls}>Order#</span>,
      key: 'orderid',
      render: (_, row) => (
        <span className="text-sm font-bold text-slate-900 dark:text-slate-100">
          {row.order.orderid}
        </span>
      ),
    },
    {
      title: <span className={headerCls}>Date</span>,
      key: 'date',
      render: (_, row) => (
        <span className="text-sm font-bold text-slate-900 dark:text-slate-100">
          {formatTime(row)}
        </span>
      ),
    },
    {
      title: <span className={headerCls}>Status</span>,
      key: 'status',
      render: (_, row) => (
        <span className="text-sm text-slate-900 dark:text-slate-100">
          {OrderStatus[row.order.orderstatus]}
        </span>
      ),
    },
    {
      title: (
        <span className={`${headerCls} inline-flex items-center`}>
          Subtotal
          {hasCrvTaxInOrders(filteredOrderList) && (
            <>
              <Tooltip id="subtotal" html />
              <InformationCircleIcon
                data-tooltip-id="subtotal"
                data-tooltip-html="Subtotal includes the Customer Fee, which is paid to Thisway by the Customer."
                className="ml-1 h-5 w-5 text-blue-500"
              />
            </>
          )}
        </span>
      ),
      key: 'subtotal',
      render: (_, row) => (
        <span className="text-sm tabular-nums text-slate-500 dark:text-slate-300">
          {formatUSD(row.order.subtotal)}
        </span>
      ),
    },
    {
      title: <span className={headerCls}>Tax</span>,
      key: 'tax',
      render: (_, row) => (
        <span className="text-sm tabular-nums text-slate-500 dark:text-slate-300">
          {formatUSD(row.order.tax)}
        </span>
      ),
    },
    showStoreFees && {
      title: <span className={headerCls}>Store Fees</span>,
      key: 'storefees',
      render: (_, row, idx) => (
        <span
          className="text-sm tabular-nums text-slate-500 dark:text-slate-300"
          data-tooltip-id={`extrafee-${idx}`}
        >
          {row.order.extrafees.length > 0 && (
            <Tooltip id={`extrafee-${idx}`} place="top" effect="solid">
              {row.order.extrafees.map((extrafee, i) => (
                <div key={i}>
                  {extrafee.name} - {formatUSD(extrafee.price)}
                </div>
              ))}
            </Tooltip>
          )}
          {formatUSD(row.order.totalextrafee)}
        </span>
      ),
    },
    isColumnVisible.itemfee && {
      title: (
        <span className={`${headerCls} inline-flex items-center`}>
          Customer Fee
          <Tooltip id="crvtax" html />
          <InformationCircleIcon
            data-tooltip-id="crvtax"
            data-tooltip-html="This Fee paid by customer to Thisway during checkout process"
            className="ml-1 h-5 w-5 text-blue-500"
          />
        </span>
      ),
      key: 'itemfee',
      render: (_, row) => (
        <span className="text-sm tabular-nums text-slate-500 dark:text-slate-300">
          ({formatUSD(row.order?.itemfee)})
        </span>
      ),
    },
    isColumnVisible.crvtax && {
      title: <span className={headerCls}>CRV Tax</span>,
      key: 'crvtax',
      render: (_, row) => (
        <span className="text-sm tabular-nums text-slate-500 dark:text-slate-300">
          {formatUSD(row.order.crvtax)}
        </span>
      ),
    },
    {
      title: <span className={headerCls}>Tip</span>,
      key: 'tip',
      render: (_, row) => (
        <span className="text-sm tabular-nums text-slate-500 dark:text-slate-300">
          {formatUSD(row.order.tip)}
        </span>
      ),
    },
    {
      title: <span className={headerCls}>Card Fees</span>,
      key: 'cardfees',
      render: (_, row) => (
        <span className="text-sm tabular-nums text-slate-500 dark:text-slate-300">
          (
          {formatUSD(
            row.order.stripefee > row.order.cardfee
              ? row.order.stripefee
              : row.order.cardfee,
          )}
          )
        </span>
      ),
    },
    {
      title: <span className={headerCls}>Discount</span>,
      key: 'discount',
      render: (_, row) => (
        <span className="text-sm tabular-nums text-slate-500 dark:text-slate-300">
          ({formatUSD(row.order.discount)})
        </span>
      ),
    },
    {
      title: <span className={headerCls}>Fees</span>,
      key: 'thiswayfee',
      render: (_, row) => (
        <span className="text-sm tabular-nums text-slate-500 dark:text-slate-300">
          ({formatUSD(row.order.thiswayfee)})
        </span>
      ),
    },
    {
      title: <span className={headerCls}>Refund</span>,
      key: 'refund',
      render: (_, row) => (
        <span className="text-sm tabular-nums text-slate-500 dark:text-slate-300">
          ({formatUSD(row.order.refund)})
        </span>
      ),
    },
    {
      title: <span className={headerCls}>Net Payout</span>,
      key: 'netpayout',
      render: (_, row) => (
        <span className="text-sm tabular-nums text-slate-500 dark:text-slate-300">
          {calcNetPayout(row)}
        </span>
      ),
    },
  ].filter(Boolean);

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-white dark:bg-slate-800 ">
      <>
        <div>
          {searchParams.get('thisWeek') && (
            <div className="mt-6 grid grid-cols-1 gap-4 text-sm leading-6 sm:grid-cols-2">
              <div className="flex items-center gap-3 sm:pr-4">
                <img
                  className="h-12 w-12 shrink-0 rounded-full object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                  src={
                    filteredOrderList[0]?.order.store.storelogo || NoStoreLogo
                  }
                  alt=""
                />
                <span className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  {searchParams.get('storeid')
                    ? filteredOrderList[0]?.order?.store?.name
                    : 'All Stores'}
                </span>
              </div>
              {loading.listLoading ? (
                <div className="space-y-2 py-1">
                  <div className="skeleton-bar h-4 w-48 rounded" />
                  <div className="skeleton-bar h-3 w-32 rounded" />
                </div>
              ) : (
                <div className="text-sm font-semibold text-slate-500 dark:text-slate-400 sm:pr-4">
                  Statement{' '}
                  {moment(thisWeekDays.startdate)
                    .startOf('isoWeek')
                    .format('MMMM DD, YYYY')}{' '}
                  -{' '}
                  {moment(thisWeekDays.enddate)
                    .endOf('isoWeek')
                    .format('MMMM DD, YYYY')}
                </div>
              )}
              {searchParams.get('thisWeek') && (
                <div className="col-span-1 flex justify-start sm:col-span-2 sm:justify-end">
                  <WeekSlider
                    onWeekChange={handleWeekChange}
                    initialDate={dayjs(searchParams.get('startdate'))}
                  />
                </div>
              )}
            </div>
          )}
          {finance?.details?.length > 0 && (
            <dl className="mt-6 grid grid-cols-1 text-sm leading-6 sm:grid-cols-2">
              <div className="sm:pr-4">
                <dd className="mt-2 text-lg text-gray-500 dark:text-slate-400">
                  <img
                    className="flex my-2 h-14 w-14 items-center justify-center rounded-full"
                    src={finance.store.storelogo || NoStoreLogo}
                    alt=""
                  />
                  <span className="font-semibold text-gray-900 dark:text-slate-200">
                    {finance.store.name}
                  </span>
                  <br />
                  {finance.store.address || null}{' '}
                  {finance.store.apartment && finance.store.apartment !== 'null'
                    ? finance.store.apartment
                    : ''}
                  {'  '}
                  <br />
                  {finance.store.city}
                  {', '}
                  {finance.store.state}
                  {'  '}
                  {finance.store.postalcode}
                </dd>
              </div>
              <div className="mt-2 sm:mt-0 sm:pl-4"></div>
              <div className="my-6 border-t border-gray-900/5 pt-6 sm:pr-4">
                <dt className="inline font-semibold text-gray-500 dark:text-slate-400">
                  Statement{' '}
                  {moment(finance.startdate)
                    .utcOffset(0)
                    .format('MMMM DD, YYYY')}{' '}
                  -{' '}
                  {moment(finance.enddate)
                    .utcOffset(0)
                    .format('MMMM DD, YYYY')}{' '}
                </dt>
              </div>
              <div className="text-end my-8 sm:my-6 sm:border-t sm:border-gray-900/5 sm:pl-4 sm:pt-6">
                <dd className="font-semibold inline text-gray-500 dark:text-slate-400">
                  <time dateTime="2023-31-01">
                    Issued On{' '}
                    {moment(finance.createdate)
                      .utcOffset(0)
                      .format('MMMM DD, YYYY')}
                  </time>
                </dd>
              </div>
            </dl>
          )}
          {loading.listLoading ? (
            <div className="w-full py-4">
              <StatGridSkeleton count={6} />
            </div>
          ) : (
            <dl className="mt-5 grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-6">
              {totalStats.map((stat, i) => (
                <div
                  key={stat.name}
                  className="overflow-hidden rounded-md bg-white px-4 py-5 shadow sm:p-6 dark:bg-slate-700"
                >
                  <dt className="truncate text-sm font-medium text-gray-500 dark:text-slate-200 dark:text-slate-200">
                    {stat.name}
                  </dt>
                  <dd className="mt-1 text-3xl font-semibold tracking-tight text-gray-900 dark:text-slate-200">
                    {stat.name === 'Total Order'
                      ? stat.value
                      : stat.value.toLocaleString('en-US', {
                          style: 'currency',
                          currency: 'USD',
                        })}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </div>
        <div className="sm:flex sm:items-center"></div>
        <div className="mt-8 flex flex-col">
          <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8 px-4">
              {loading.listLoading ? (
                <DataTableSkeleton
                  rows={6}
                  headers={[
                    { label: 'Order#', w: 'w-12' },
                    { label: 'Date', w: 'w-32' },
                    { label: 'Status', w: 'w-20' },
                    { label: 'Subtotal', w: 'w-16' },
                    { label: 'Tax', w: 'w-12' },
                    showStoreFees && { label: 'Store Fees', w: 'w-16' },
                    isColumnVisible.itemfee && {
                      label: 'Customer Fee',
                      w: 'w-16',
                    },
                    isColumnVisible.crvtax && { label: 'CRV Tax', w: 'w-12' },
                    { label: 'Tip', w: 'w-12' },
                    { label: 'Card Fees', w: 'w-16' },
                    { label: 'Discount', w: 'w-16' },
                    { label: 'Fees', w: 'w-16' },
                    { label: 'Refund', w: 'w-16' },
                    { label: 'Net Payout', w: 'w-20' },
                  ].filter(Boolean)}
                />
              ) : filteredOrderList.length === 0 ? (
                <div className="grid justify-center py-20">
                  <img src={NoItems} className="h-24 pb-2" alt="" />
                  <p className=" text-slate-400 font-light text-center">
                    No Orders
                  </p>
                </div>
              ) : (
                <TWDataTable
                  columns={orderColumns}
                  data={filteredOrderList}
                  rowKey={(row, idx) => row.order?.id || idx}
                  pagination={
                    pagination.totalRecords > pagination.pageSize
                      ? {
                          current: pagination.pageNumber,
                          pageSize: pagination.pageSize,
                          total: pagination.totalRecords,
                          showTotal: (total, range) =>
                            `Showing ${range[0]} to ${range[1]} of ${total} orders`,
                          onChange: (pageNumber, pageSize) =>
                            handleChangePage(pageNumber, pageSize),
                          showSizeChanger: false,
                        }
                      : undefined
                  }
                />
              )}
            </div>
          </div>
        </div>
      </>
      <Notification
        display={notf.display}
        type={notf.type}
        message={notf.message}
        onClose={() => setNotf((prev) => ({ ...prev, display: false }))}
      />
    </div>
  );
};

export default OrderList;
